import newSystemEn from './newsystem-en'

const en = {
    campus : "University Name",
    faculty: "Faculty",
    department: "Department",
    academicYear:"Academic Year",
    semester:"Semester",
    course:"Select Course",

    button: {
        setting:"Setting",
        create :"Create"
    },
    description : "Description",
    preferences: {
        title: "Preferences",
        subtitle: "Manage workspace behavior, notifications, layout defaults, and privacy options for this browser.",
        workspaceSettings: "Workspace Settings",
        personalPreferences: "Personal Preferences",
        personalPreferencesCopy: "These settings are saved locally in your browser, so you can tune the workspace without affecting other users.",
        currentState: "Current State",
        recommendations: "Recommendations",
        whatSavesHere: "What Saves Here",
        storageNote: "These preferences are stored in local browser storage only. They do not change other accounts or global system defaults.",
        save: "Save Preferences",
        reset: "Reset to Default",
        enabled: "Enabled",
        disabled: "Disabled",
        standard: "Standard",
        reduced: "Reduced",
        expanded: "Expanded",
        compact: "Compact",
        languageEnglish: "English",
        languageThai: "Thai",
        time24: "24-hour",
        time12: "12-hour",
        sections: {
            languageRegion: "Language & Region",
            languageRegionCopy: "Choose how the workspace should present labels and timestamps.",
            notifications: "Notifications",
            notificationsCopy: "Control how much activity reaches you while working in the system.",
            layoutNavigation: "Layout & Navigation",
            layoutNavigationCopy: "Set your preferred workspace defaults for navigation and browsing.",
            privacySession: "Privacy & Session",
            privacySessionCopy: "Choose how much local activity should be remembered on this device."
        },
        fields: {
            language: "Language",
            timeFormat: "Time format",
            defaultLandingPage: "Default landing page",
            resultsPerPage: "Results per page",
            emailAlerts: "Email alerts",
            desktopAlerts: "Desktop alerts",
            reviewAlerts: "Review alerts",
            sidebarMemory: "Sidebar memory",
            landingPage: "Landing page"
        },
        toggles: {
            emailNotificationsTitle: "Email notifications",
            emailNotificationsCopy: "Receive summary and workflow updates through email.",
            desktopNotificationsTitle: "Desktop notifications",
            desktopNotificationsCopy: "Show browser notifications for new reviews and status changes.",
            reviewAlertsTitle: "Review alerts",
            reviewAlertsCopy: "Highlight new committee comments and assignment activity.",
            translationAlertsTitle: "Translation alerts",
            translationAlertsCopy: "Notify when translation requests move to a new status.",
            rememberSidebarTitle: "Remember sidebar state",
            rememberSidebarCopy: "Keep the sidebar expanded or minimized the way you left it.",
            startMinimizedSidebarTitle: "Start with minimized sidebar",
            startMinimizedSidebarCopy: "Use a compact navigation layout by default.",
            keepNavigationContextTitle: "Open links in current workspace",
            keepNavigationContextCopy: "Prefer staying inside the current app flow instead of opening extra tabs.",
            rememberRecentPagesTitle: "Remember recent pages",
            rememberRecentPagesCopy: "Store quick access history for faster return navigation.",
            storeDraftRecoveryTitle: "Store draft recovery hints",
            storeDraftRecoveryCopy: "Keep local recovery cues for unfinished work in this browser.",
            confirmBeforeSignOutTitle: "Show confirmation before sign out",
            confirmBeforeSignOutCopy: "Add an extra confirmation step before leaving the workspace."
        },
        stats: {
            alertsOn: "Alerts On",
            alertsOnHint: "Notification channels enabled",
            layout: "Layout",
            layoutHint: "Default sidebar mode",
            privacy: "Privacy",
            privacyHint: "Local memory behavior"
        },
        tips: {
            focusedReviewerTitle: "Focused Reviewer",
            focusedReviewerCopy: "Enable review alerts and remember sidebar state to keep the queue easy to monitor.",
            frequentDrafterTitle: "Frequent Drafter",
            frequentDrafterCopy: "Store draft recovery hints and set a landing page that opens directly to agreements.",
            sharedDeviceTitle: "Shared Device",
            sharedDeviceCopy: "Turn off recent page memory if this browser is used by more than one person."
        },
        landingOptions: {
            dashboard: "Dashboard",
            myAgreements: "My Agreements",
            myReviewQueue: "My Review Queue",
            myTranslationRequests: "My Translation Requests",
            myReports: "My Reports",
            committeeDashboard: "Committee Dashboard",
            review: "Review",
            reviewHistory: "Review History",
            guideline: "Guideline"
        }
    },
    shell: {
        dashboard: "Dashboard",
        signOut: "Sign Out",
        profile: "Profile",
        preferences: "Preferences",
        general: "General",
        permission: "Permission",
        admin: "Admin",
        committee: "Committee",
        user: "User",
        multiUser: "Workspace",
        navigation: {
            allAgreements: "All Agreements",
            auditLog: "Audit Log",
            clauseLibrary: "Clause Library",
            reviewQueue: "Review Queue",
            translationRequests: "Translation Requests",
            reportsAnalytics: "Reports & Analytics",
            accountDirectory: "Account Directory",
            rolePermissionMatrix: "Role Permission Matrix",
            reviewAssignments: "Review Assignments",
            createMenu: "Create Menu",
            createGroup: "Create Group",
            review: "Review",
            reviewHistory: "Review History",
            userManual: "User Manual",
            helpSupport: "Help & Support",
            myAgreements: "My Agreements",
            myClauseLibrary: "My Clause Library",
            myReviewQueue: "My Review Queue",
            myTranslationRequests: "My Translation Requests",
            myReports: "My Reports"
        }
    },
    common: {
        lastUpdated: "Last updated",
        refresh: "Refresh"
    },
    routes: {
        dashboard: { title: "Dashboard", description: "Overview of the active workspace and available role dashboards." },
        adminDashboard: { title: "Admin Dashboard", description: "System-wide visibility for agreements, permissions, analytics, and audit activity." },
        committeeDashboard: { title: "Committee Dashboard", description: "Review assignments, decisions, history, and guideline updates for committee members." },
        review: { title: "Review", description: "Open assigned cases and complete committee review actions." },
        reviewHistory: { title: "Review History", description: "Look back through completed reviews and prior committee decisions." },
        guideline: { title: "Guideline", description: "Publish and review operational guidance, rules, and reference material." },
        help: { title: "Help & Support", description: "Provide FAQs, contact channels, and support resources for users." },
        userAgreements: { title: "My Agreements", description: "Create, submit, and track your agreement workflow." },
        userClauseLibrary: { title: "My Clause Library", description: "Reuse starter clauses and manage your own clause library." },
        userReviewQueue: { title: "My Review Queue", description: "Follow agreement progress through admin and committee review." },
        userTranslation: { title: "My Translation Requests", description: "Request translation support for agreement drafts and track progress." },
        userReports: { title: "My Reports", description: "Review your agreement and translation activity in one place." },
        userGuideline: { title: "User Guideline", description: "Read the current user workflow and agreement guidance." },
        userNewAgreement: { title: "Create Agreement", description: "Start a new agreement draft." },
        profile: { title: "Profile", description: "Update personal details and review your workspace profile settings." },
        preferences: { title: "Preferences", description: "Manage notifications, language, and default workspace behavior." },
        notifications: { title: "Notifications", description: "Review alerts, reminders, and unread updates in one place." }
    },
    pages: {
        user: {
            agreements: {
                title: "My Agreements",
                subtitle: "Create, edit, submit, and track agreements through the full review workflow."
            },
            clauseLibrary: {
                title: "My Clause Library",
                subtitle: "Browse starter clauses and your saved clause selections for agreement drafting."
            },
            reviewQueue: {
                title: "My Review Queue",
                subtitle: "Track when your agreement is waiting for admin, committee suggestions, or final decision."
            },
            translation: {
                title: "My Translation Requests",
                subtitle: "Request translation support for agreement drafts, review request status, and keep your language work organized in one place."
            },
            newAgreement: {
                title: "Create New Agreement",
                subtitle: "Choose the agreement type first. You can decide later whether to use a template, write your own draft, or import a contract."
            },
            reports: {
                title: "My Reports",
                subtitle: "Track your agreement volume, geography, department ownership, and workflow movement from your own agreement records.",
                range: "Range"
            },
            guideline: {
                title: "User Guideline",
                subtitle: "Keep the agreement workflow, translation support steps, and submission rules easy to follow.",
                currentVersion: "Current Version",
                sections: {
                    workflow: "Workflow Steps",
                    resources: "Helpful Resources",
                    faq: "Frequently Asked Questions"
                }
            }
        },
        dashboard: {
            user: {
                heroKicker: "User Dashboard",
                heroTitle: "Keep your agreement workflow moving",
                heroCopy: "Follow your drafts from submission, committee review, revision rounds, and final admin decision in one place.",
                myAgreements: "My Agreements",
                myReviewQueue: "My Review Queue",
                translationRequests: "Translation Requests",
                inReview: "Review",
                inReviewCopy: "Agreements currently waiting on admin assignment, committee review, or final admin decision.",
                monthlyActivityTitle: "Monthly Activity",
                monthlyActivitySubtitle: "Agreement and translation movement across recent months",
                agreementStatusTitle: "Agreement Status",
                agreementStatusSubtitle: "Overview of your agreements by approval status",
                quickActionsTitle: "Quick Actions",
                quickActionsSubtitle: "Jump straight to your next task",
                recentAgreementsTitle: "Recent Agreements",
                recentAgreementsSubtitle: "Your latest agreement items and their current stage",
                recentTranslationsTitle: "Recent Translation Requests",
                recentTranslationsSubtitle: "Support requests tied to your agreement work",
                drafts: "Drafts",
                draftsDelta: "Drafts you can still edit before submission",
                reviewCount: "Review",
                reviewCountDelta: "Items moving between admin assignment, committee review, and final approval",
                approved: "Approved",
                approvedDelta: "Completed agreements already cleared",
                rejected: "Rejected",
                active: "On Progress",
                agreementsDataset: "Agreements",
                translationsDataset: "Translations",
                createAgreement: "Create Agreement",
                createAgreementCopy: "Start a new agreement draft",
                openReviewQueue: "Open Review Queue",
                openReviewQueueCopy: "Check committee comments, revision requests, and final admin status",
                requestTranslation: "Request Translation",
                requestTranslationCopy: "Send a document for translation support",
                open: "Open",
                noAgreementsYet: "No agreements yet.",
                noTranslationRequestsYet: "No translation requests yet.",
                justNow: "Just now"
            },
            committee: {
                heroKicker: "Committee Dashboard",
                heroTitle: "Keep committee reviews moving with a clear overview",
                heroCopy: "Track the shared committee review queue, urgent cases, completion pace, and the next review actions without leaving the committee workspace.",
                openReviewPage: "Open Review Page",
                reviewHistory: "Review History",
                guideline: "Guideline",
                deadlineEyebrow: "Urgent Deadline Reminder",
                openReview: "Open Review",
                panelActivityTitle: "Committee Activity Overview",
                panelActivitySubtitle: "How your current review workload is distributed and where attention is needed next",
                chartHeadline: "Committee workload distribution across the current review queue.",
                currentFocus: "Current Focus",
                currentFocusValue: "{count} active reviews moving now",
                dueSoon: "Due Soon",
                dueSoonValue: "{count} items need attention soon",
                completedThisWeek: "Completed This Week",
                completedThisWeekValue: "+{count} reviews closed",
                quickActionsTitle: "Quick Actions",
                quickActionsSubtitle: "Jump straight to the next committee task",
                queueTitle: "Active Review Queue",
                queueSubtitle: "Committee-stage agreements that still need review work",
                highPriorityDeadline: "High priority deadline",
                open: "Open",
                noQueueItems: "No committee queue items are active right now.",
                workloadBreakdown: "Workload Breakdown",
                workloadBreakdownSubtitle: "How committee effort is currently distributed",
                deadlinePressure: "Deadline Pressure",
                deadlinePressureSubtitle: "Reviews that need close attention now",
                currentFocusCard: "Current Focus",
                currentFocusCardSubtitle: "What the committee is actively moving today",
                pending: "Pending",
                inProgress: "In Progress",
                sentToAdmin: "Sent to Admin",
                completed: "Completed",
                topUrgentItem: "Top Urgent Item",
                noUrgentItems: "No urgent items",
                daysLeft: "Days Left",
                stable: "Stable",
                startedToday: "Started Today",
                averageReviewTime: "Avg. Review Time",
                activeAgreement: "Active Agreement",
                noActiveReview: "No active review",
                openReviewPageCopy: "Start or continue active committee review work",
                reviewHistoryCopy: "Look back at completed committee decisions",
                guidelineCopy: "Open committee guidance and reference notes",
                pendingReview: "Pending Review",
                pendingReviewDelta: "{count} due soon",
                inProgressDelta: "{count} started today",
                sentToAdminDelta: "Waiting for final admin decision",
                completedDelta: "+{count} this week",
                averageReviewTimeDelta: "Current cycle",
                statusPending: "Pending Review",
                statusInProgress: "In Progress",
                statusCompleted: "Completed",
                dueToday: "Due today",
                oneDayLeft: "1 day left",
                daysLeftValue: "{count} days left",
                inReviewChart: "Review",
                sentToAdminChart: "Sent to Admin",
                completedChart: "Completed",
                committeeReviewsDataset: "Committee Reviews",
                justNow: "Just now",
                highPriorityTitle: "{agreement} is now high priority",
                highPriorityText: "Committee review deadlines are 7 days from assignment. This item has {timeRemaining} remaining, so it needs attention now.",
                dueTodayBadge: "Due today",
                daysLeftBadge: "{count} days left"
            },
            admin: {
                heroKicker: "Administrator Dashboard",
                heroTitle: "Welcome back, Admin",
                heroCopy: "Keep track of agreements, review progress, trusted devices, and the key admin work surrounding each MOU.",
                viewAgreements: "View Agreements",
                reviewQueue: "Review Queue",
                openReports: "Open Reports",
                justNow: "Just now"
            }
        }
    }
}

function mergeLocale(target, source) {
    Object.keys(source || {}).forEach((key) => {
        const sourceValue = source[key]
        const targetValue = target[key]
        if (
            sourceValue &&
            typeof sourceValue === 'object' &&
            !Array.isArray(sourceValue) &&
            targetValue &&
            typeof targetValue === 'object' &&
            !Array.isArray(targetValue)
        ) {
            mergeLocale(targetValue, sourceValue)
            return
        }
        target[key] = sourceValue
    })
    return target
}

mergeLocale(en, newSystemEn)

export default en
